<script>
import { layoutMethods, transactionMonitoringMethods } from "@/state/helpers";
import Swal from "sweetalert2";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import {
  ACTION_BADGES,
  RULE_LABELS,
  TIER_LABELS,
  FREEZE_REASONS,
  UNFREEZE_REASONS,
  ACCOUNT_ACTIONS,
  ACCOUNT_TYPES,
} from "./mockData";

const PRODUCT_LABELS = { peddlr_lite: "Kankolek", peddlr: "Peddlr" };
const MAX_FILE_MB = 15;

export default {
  page: { title: "Transaction Monitoring" },
  components: { flatPickr },
  data() {
    return {
      activeTab: "user-wallet", // 'user-wallet' | 'logs'

      // ---- User Wallet (evaluations) ----
      evalList: [],
      evalTotal: 0,
      evalLoading: false,
      searchField: "all",
      searchKeyword: "",
      filterStatus: "all",
      filterPriority: "all",
      filterAction: "all",
      filterAccountType: "all",
      page: 1,
      perPage: 8,
      pageOptions: [8, 10, 20, 30, 50],

      // All search fields are filtered server-side (via the tm_merchant_wallet JOIN in get-tm-evaluations),
      // so every option paginates correctly. "Search All" OR-matches number/name/business.
      searchFieldOptions: [
        { value: "all", text: "Search All" },
        { value: "account_number", text: "Account Number" },
        { value: "account_name", text: "Account Name" },
        { value: "business_name", text: "Business Name" },
      ],
      statusOptions: [
        { value: "all", text: "All" },
        { value: "enabled", text: "Enabled" },
        { value: "freeze", text: "Freeze" },
      ],
      priorityOptions: [
        { value: "all", text: "All" },
        { value: "low", text: "Low" },
        { value: "medium", text: "Medium" },
        { value: "high", text: "High" },
        { value: "critical", text: "Critical" },
      ],
      accountTypeOptions: [
        { value: "all", text: "All" },
        { value: "micro", text: "Micro" },
        { value: "small", text: "Small" },
        { value: "medium", text: "Medium" },
        { value: "corporate", text: "Corporate" },
      ],
      // Action Required options — derived 1:1 from the VM ACTION enum (ACTION_BADGES), plus "All".
      actionOptions: [
        { value: "all", text: "All" },
        ...Object.keys(ACTION_BADGES).map((v) => ({ value: v, text: ACTION_BADGES[v].label })),
      ],

      // ---- Logs (unified) ----
      logList: [],
      logTotal: 0,
      logLoading: false,
      logSearchKeyword: "",
      logDateRange: "",
      logFilterStatus: "all",
      logPage: 1,
      logPerPage: 8,
      logRangeCfg: { mode: "range", maxDate: "today" },

      // ---- Status modal (freeze / unfreeze) ----
      modal: {
        show: false,
        mode: "freeze", // 'freeze' | 'unfreeze'
        tab: "details", // 'details' | 'documents'
        row: null,
        balance: null,
        balanceLoading: false,
        submitting: false,
        reason: "",
        initialReport: false,
        accountAction: "",
        accountType: "",
        remarks: "",
        files: [], // { name, size_label, progress, done }
        docHistory: [],
      },

      // ---- View Log modal ----
      logModal: {
        show: false,
        row: null,
        logs: [],
        loading: false,
        dateRange: "",
        filterStatus: "all",
        page: 1,
        perPage: 8,
        total: 0,
      },
      logModalRangeCfg: { mode: "range", maxDate: "today" },

      FREEZE_REASONS,
      UNFREEZE_REASONS,
      ACCOUNT_ACTIONS,
      ACCOUNT_TYPES,
    };
  },
  computed: {
    recordText() {
      return `${this.evalTotal} Record(s)`;
    },
    // Pagination is server-side: the backend returns exactly the current page.
    pagedEvals() {
      return this.evalList;
    },
    pagedLogs() {
      return this.logList;
    },
    // Initial Report only applies to Fraudulent Activity (required by default, documents to follow).
    // Every other reason has no Initial Report and an optional upload.
    isFraudulentActivity() {
      return this.modal.reason === "Fraudulent Activity";
    },
    // Freeze: documents are only required for Fraudulent Activity, and only when Initial Report is NOT
    // checked. For all other reasons the upload is optional.
    freezeDocsRequired() {
      return this.isFraudulentActivity && !this.modal.initialReport;
    },
    canConfirm() {
      const m = this.modal;
      if (!m.reason) return false;
      if (m.mode === "freeze") {
        if (this.freezeDocsRequired && m.files.length === 0) return false;
        return true;
      }
      // unfreeze
      if (!m.accountAction) return false;
      if (!m.accountType) return false;
      if (m.files.length === 0) return false; // docs required on unfreeze
      return true;
    },
  },
  watch: {
    // Initial Report defaults to CHECKED for Fraudulent Activity (required by default; documents to follow)
    // and is cleared for every other reason (those reasons have no Initial Report at all).
    "modal.reason"(reason) {
      this.modal.initialReport = reason === "Fraudulent Activity";
    },
    page() {
      this.fetchEvals();
    },
    perPage() {
      this.page = 1;
      this.fetchEvals();
    },
    logPage() {
      this.fetchLogs();
    },
    logPerPage() {
      this.logPage = 1;
      this.fetchLogs();
    },
    "logModal.page"() {
      this.fetchLogModalLogs();
    },
    "logModal.perPage"() {
      this.logModal.page = 1;
      this.fetchLogModalLogs();
    },
  },
  async mounted() {
    this.changeTitle({ title: "Transaction Monitoring" });
    this.changeBreadCrumb({
      breadCrumbs: [
        { text: "Peddlr", to: { name: "Home" } },
        { text: "Kankolek Manager", to: { name: "" } },
        { text: "Transaction Monitoring", to: { name: "" } },
      ],
    });
    document.addEventListener("keydown", this.onEsc);
    await this.fetchEvals();
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onEsc);
  },
  methods: {
    ...layoutMethods,
    ...transactionMonitoringMethods,

    // ---------- labels / badges ----------
    ruleLabel(code) {
      if (!code) return "";
      return RULE_LABELS[code] || code;
    },
    actionBadge(action) {
      return action ? ACTION_BADGES[action] : null;
    },
    tierLabel(tier) {
      return tier ? TIER_LABELS[tier] || tier : "--";
    },
    productLabel(p) {
      return PRODUCT_LABELS[p] || p;
    },
    priorityOf(action) {
      const b = this.actionBadge(action);
      return b ? b.severity : "";
    },
    priorityText(action) {
      const s = this.priorityOf(action);
      return s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
    },

    // ---------- evaluations (list-evaluations) ----------
    async fetchEvals() {
      this.evalLoading = true;
      try {
        const res = await this.actionTransactionMonitoring({
          action: "list-evaluations",
          body: {
            page: this.page,
            limit: this.perPage,
            search_field: this.searchField,
            search_keyword: this.searchKeyword,
            filter_status: this.filterStatus,
            filter_priority: this.filterPriority,
            filter_action: this.filterAction,
            filter_account_type: this.filterAccountType,
          },
        });
        if (res.status === "ok") {
          this.evalList = res.message?.data || [];
          this.evalTotal = res.message?.total || 0;
        }
      } catch (err) {
        console.error("list-evaluations failed:", err);
      } finally {
        this.evalLoading = false;
      }
    },
    onSearch() {
      this.page = 1;
      this.fetchEvals();
    },
    onRefresh() {
      this.searchKeyword = "";
      this.filterStatus = "all";
      this.filterPriority = "all";
      this.filterAction = "all";
      this.filterAccountType = "all";
      this.page = 1;
      this.fetchEvals();
    },
    async onExport() {
      try {
        await this.actionTransactionMonitoring({
          action: "export-evaluations",
          body: {
            search_field: this.searchField,
            search_keyword: this.searchKeyword,
            filter_status: this.filterStatus,
            filter_priority: this.filterPriority,
            filter_action: this.filterAction,
            filter_account_type: this.filterAccountType,
          },
        });
        Swal.fire("Success!", "Your export request has been queued. It will be emailed when ready.", "success");
      } catch (err) {
        Swal.fire("Failed!", "Your export request has failed.", "warning");
      }
    },

    // ---------- status toggle -> open modal ----------
    toggleStatus(row) {
      const mode = row.wallet_status === "enabled" ? "freeze" : "unfreeze";
      this.openStatusModal(row, mode);
    },
    openStatusModal(row, mode) {
      this.modal = {
        show: true,
        mode,
        tab: "details",
        row,
        balance: null,
        balanceLoading: true,
        submitting: false,
        reason: "",
        initialReport: false,
        accountAction: "",
        accountType: row.kyc_tier || "",
        remarks: "",
        files: [],
        docHistory: [],
      };
      // balance for the modal header. user_id/business_id are resolved server-side from cognito_sub (the
      // list no longer carries them — identity for display comes from the tm_merchant_wallet join).
      this.actionTransactionMonitoring({
        action: "get-balance",
        body: { cognito_sub: row.cognito_sub, product: row.product },
      })
        .then((res) => {
          if (res.status === "ok") this.$set(this.modal, "balance", res.message?.balance ?? 0);
        })
        .catch(() => this.$set(this.modal, "balance", 0))
        .finally(() => {
          this.modal.balanceLoading = false;
        });
      // documents history
      this.actionTransactionMonitoring({
        action: "list-documents-history",
        body: { cognito_sub: row.cognito_sub, product: row.product },
      })
        .then((res) => {
          if (res.status === "ok") this.modal.docHistory = res.message?.data || [];
        })
        .catch(() => {
          this.modal.docHistory = [];
        });
    },
    closeStatusModal() {
      this.modal.show = false;
    },
    // original freeze reason for the unfreeze header (from latest freeze in history)
    originalFreezeReason() {
      const h = this.modal.docHistory.find((d) => d.status === "freeze");
      return h ? h.reason : "--";
    },

    // ---------- mock uploader ----------
    // ---------- uploader (direct-to-S3 via pre-signed PUT) ----------
    onFilePick(e) {
      const picked = Array.from(e.target.files || []);
      picked.forEach((f) => this.uploadFile(f));
      e.target.value = "";
    },
    async uploadFile(f) {
      const mb = f.size / (1024 * 1024);
      if (mb > MAX_FILE_MB) {
        Swal.fire("File too large", `${f.name} exceeds ${MAX_FILE_MB}MB.`, "warning");
        return;
      }
      const entry = {
        name: f.name,
        size_label: `${mb.toFixed(1)} MB`,
        progress: 0,
        done: false,
        documentId: null,
        failed: false,
      };
      this.modal.files.push(entry);
      try {
        const res = await this.actionTransactionMonitoring({
          action: "presign-upload",
          body: {
            cognito_sub: this.modal.row.cognito_sub,
            product: this.modal.row.product,
            file_name: f.name,
            content_type: f.type || "application/octet-stream",
            size_bytes: f.size,
          },
        });
        if (!res || res.status !== "ok" || !res.message || !res.message.uploadUrl) {
          throw new Error("Presign returned no uploadUrl.");
        }
        entry.documentId = res.message.documentId;
        // PUT the bytes straight to S3 using native fetch (NOT axios): the app mutates axios global
        // defaults (axios.defaults.headers = { Authorization: token }), and that bleeds the ERP bearer
        // token into the S3 PUT — S3 then rejects "Only one auth mechanism allowed". fetch inherits
        // nothing, so the ONLY auth S3 sees is the pre-signed query string. Content-Type must match what
        // was signed (the same value the presign used).
        const putRes = await fetch(res.message.uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": f.type || "application/octet-stream" },
          body: f,
        });
        if (!putRes.ok) {
          const bodyText = await putRes.text().catch(() => "");
          const err = new Error(`S3 PUT ${putRes.status}`);
          err.s3Status = putRes.status;
          err.s3Body = bodyText;
          throw err;
        }
        entry.progress = 100;
        entry.done = true;
      } catch (err) {
        // S3 responded with an error (signature/permission) -> status + XML body; otherwise a network/CORS
        // failure (fetch throws a TypeError with no status).
        if (err && err.s3Status) {
          console.error("S3 upload rejected:", err.s3Status, err.s3Body);
        } else {
          console.error("S3 upload network/CORS error (no response):", err && err.message);
        }
        entry.failed = true;
        Swal.fire("Upload failed", `Could not upload ${f.name}. Please try again.`, "warning");
      }
    },
    removeFile(idx) {
      this.modal.files.splice(idx, 1);
    },

    // ---------- confirm (freeze / unfreeze) ----------
    async confirmStatus() {
      if (!this.canConfirm) return;
      // Guard: wait for any in-flight uploads to finish.
      if (this.modal.files.some((f) => !f.done && !f.failed)) {
        Swal.fire("Please wait", "Documents are still uploading.", "info");
        return;
      }
      const documentIds = this.modal.files.filter((f) => f.done && f.documentId).map((f) => f.documentId);
      const row = this.modal.row;
      this.modal.submitting = true;
      try {
        const action = this.modal.mode === "freeze" ? "freeze" : "unfreeze";
        // user_id/business_id are resolved server-side from cognito_sub (+ product).
        const body = {
          cognito_sub: row.cognito_sub,
          product: row.product,
          reason: this.modal.reason,
          remarks: this.modal.remarks,
          document_ids: documentIds,
        };
        if (this.modal.mode === "freeze") {
          body.initial_report = this.modal.initialReport;
        } else {
          body.account_action = this.modal.accountAction;
          body.account_type = this.modal.accountType;
        }
        const res = await this.actionTransactionMonitoring({ action, body });
        if (res.status !== "ok") throw new Error(res.message || "action failed");

        const newStatus = this.modal.mode === "freeze" ? "freeze" : "enabled";
        this.modal.submitting = false;
        this.modal.show = false;

        const label = newStatus === "freeze" ? "freeze" : "enabled";
        await Swal.fire({
          title: "Update Successful!",
          text: `${row.business_name} has been updated. The ${this.productLabel(row.product)} wallet has been ${label}.`,
          icon: "success",
          confirmButtonText: "OK",
        });
        // Refresh so all rows for this merchant reflect the shared new status.
        this.fetchEvals();
      } catch (err) {
        this.modal.submitting = false;
        Swal.fire("Error", typeof err === "string" ? err : "Failed to update wallet status. Please try again.", "error");
      }
    },

    // ---------- logs (get-logs, unified both products) ----------
    async fetchLogs() {
      this.logLoading = true;
      try {
        const res = await this.actionTransactionMonitoring({
          action: "get-logs",
          body: {
            page: this.logPage,
            limit: this.logPerPage,
            search_keyword: this.logSearchKeyword,
            date_range: this.logDateRange,
            filter_action: this.logFilterStatus,
            product: "peddlr_lite",
          },
        });
        if (res.status === "ok") {
          this.logList = res.message?.data || [];
          this.logTotal = res.message?.total || 0;
        }
      } catch (err) {
        console.error("get-logs failed:", err);
      } finally {
        this.logLoading = false;
      }
    },
    switchTab(tab) {
      this.activeTab = tab;
      if (tab === "logs" && this.logList.length === 0) this.fetchLogs();
    },
    onLogSearch() {
      this.logPage = 1;
      this.fetchLogs();
    },
    async onLogExport() {
      try {
        await this.actionTransactionMonitoring({
          action: "export-logs",
          body: {
            search_keyword: this.logSearchKeyword,
            date_range: this.logDateRange,
            filter_action: this.logFilterStatus,
            product: "peddlr_lite",
          },
        });
        Swal.fire("Success!", "Your export request has been queued. It will be emailed when ready.", "success");
      } catch (err) {
        Swal.fire("Failed!", "Your export request has failed.", "warning");
      }
    },

    // ---------- view log modal (per merchant) ----------
    viewLog(row) {
      this.logModal = {
        show: true,
        row,
        logs: [],
        loading: true,
        dateRange: "",
        filterStatus: "all",
        page: 1,
        perPage: 8,
        total: 0,
      };
      this.fetchLogModalLogs();
    },
    // Fetch this merchant's activity logs (server-side paginated) applying the modal's own date-range +
    // wallet-status filters. Used on open, on Search, and on page change.
    fetchLogModalLogs() {
      const row = this.logModal.row;
      if (!row) return;
      this.logModal.loading = true;
      this.actionTransactionMonitoring({
        action: "get-logs",
        body: {
          user_id: row.user_id,
          product: row.product,
          page: this.logModal.page,
          limit: this.logModal.perPage,
          date_range: this.logModal.dateRange,
          filter_action: this.logModal.filterStatus,
        },
      })
        .then((res) => {
          if (res.status === "ok") {
            this.logModal.logs = res.message?.data || [];
            this.logModal.total = res.message?.total || 0;
          }
        })
        .catch((err) => console.error("view-log failed:", err))
        .finally(() => {
          this.logModal.loading = false;
        });
    },
    onLogModalSearch() {
      this.logModal.page = 1;
      this.fetchLogModalLogs();
    },
    async onLogModalExport() {
      const row = this.logModal.row;
      if (!row) return;
      try {
        await this.actionTransactionMonitoring({
          action: "export-logs",
          body: {
            user_id: row.user_id,
            product: row.product,
            date_range: this.logModal.dateRange,
            filter_action: this.logModal.filterStatus,
          },
        });
        Swal.fire("Success!", "Your export request has been queued. It will be emailed when ready.", "success");
      } catch (err) {
        Swal.fire("Failed!", "Your export request has failed.", "warning");
      }
    },
    closeLogModal() {
      this.logModal.show = false;
    },

    // ---------- document download (signed URL) ----------
    async downloadDoc(documentId) {
      try {
        const res = await this.actionTransactionMonitoring({
          action: "download-document",
          body: { document_id: documentId },
        });
        if (res.status === "ok" && res.message?.url) {
          window.open(res.message.url, "_blank");
        }
      } catch (err) {
        Swal.fire("Failed", "Could not generate the download link.", "warning");
      }
    },

    statusText(action) {
      return action === "freeze" ? "Freezed" : "Enabled";
    },
    formatCurrency(amount) {
      if (amount == null) return "PHP 0.00";
      return `PHP ${Number(amount).toLocaleString("en-PH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
    onEsc(e) {
      if (e.key === "Escape") {
        if (this.modal.show) this.modal.show = false;
        else if (this.logModal.show) this.logModal.show = false;
      }
    },
  },
};
</script>

<template>
  <div class="row">
    <div class="col-12">
      <div class="card">
        <div class="card-body">
          <!-- Tabs -->
          <div class="tm-tabs mb-4">
            <button class="tab-btn" :class="{ active: activeTab === 'user-wallet' }" @click="switchTab('user-wallet')">
              User Wallet
            </button>
            <button class="tab-btn" :class="{ active: activeTab === 'logs' }" @click="switchTab('logs')">
              Logs
            </button>
          </div>

          <!-- ============ USER WALLET TAB ============ -->
          <div v-if="activeTab === 'user-wallet'">
            <div class="d-flex align-items-center justify-content-between mb-4">
              <h5 class="mb-0 fw-bold">{{ recordText }}</h5>
              <button class="btn btn-refresh" @click="onRefresh">
                <i class="bx bx-refresh"></i>&nbsp;Refresh
              </button>
            </div>

            <!-- Filters -->
            <div class="d-flex flex-wrap align-items-start gap-3 mb-4">
              <div class="search-group">
                <div class="input-group">
                  <select class="form-select search-field-select" v-model="searchField">
                    <option v-for="o in searchFieldOptions" :key="o.value" :value="o.value">{{ o.text }}</option>
                  </select>
                  <input type="text" class="form-control" v-model="searchKeyword" @keyup.enter="onSearch" />
                </div>
                <small class="d-block mt-1">Search Keyword</small>
              </div>

              <div class="filter-group">
                <select class="form-select" v-model="filterStatus"><option v-for="o in statusOptions" :key="o.value" :value="o.value">{{ o.text }}</option></select>
                <small class="d-block mt-1">Filter by Wallet Status</small>
              </div>
              <div class="filter-group">
                <select class="form-select" v-model="filterPriority"><option v-for="o in priorityOptions" :key="o.value" :value="o.value">{{ o.text }}</option></select>
                <small class="d-block mt-1">Filter by Priority Level</small>
              </div>
              <div class="filter-group">
                <select class="form-select" v-model="filterAccountType"><option v-for="o in accountTypeOptions" :key="o.value" :value="o.value">{{ o.text }}</option></select>
                <small class="d-block mt-1">Filter by Account Type</small>
              </div>
              <div class="filter-group">
                <select class="form-select" v-model="filterAction"><option v-for="o in actionOptions" :key="o.value" :value="o.value">{{ o.text }}</option></select>
                <small class="d-block mt-1">Filter by Action Required</small>
              </div>

              <div class="d-flex gap-2">
                <button class="btn btn-pink" @click="onSearch"><i class="bx bx-search"></i> Search</button>
                <button class="btn btn-pink" @click="onExport"><i class="bx bx-upload"></i> Export</button>
              </div>
            </div>

            <!-- Table -->
            <div class="table-responsive">
              <table class="table align-middle tm-table">
                <thead>
                  <tr>
                    <th>Account Number</th>
                    <th>Account Name</th>
                    <th>Business Name</th>
                    <th>Wallet Status</th>
                    <th>Rule ID</th>
                    <th>Priority Level</th>
                    <th>Action Required</th>
                    <th>Account Type</th>
                    <th>Activity Logs</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in pagedEvals" :key="row.eval_uuid">
                    <td>{{ row.account_number }}</td>
                    <td>{{ row.account_name }}</td>
                    <td>{{ row.business_name }}</td>
                    <td>
                      <div class="status-toggle">
                        <label class="switch">
                          <input type="checkbox" :checked="row.wallet_status === 'enabled'" @change="toggleStatus(row)" />
                          <span class="slider" :class="row.wallet_status === 'enabled' ? 'on' : 'off'"></span>
                        </label>
                        <span :class="row.wallet_status === 'enabled' ? 'txt-enabled' : 'txt-freeze'">
                          {{ row.wallet_status === 'enabled' ? 'Enabled' : 'Freeze' }}
                        </span>
                      </div>
                    </td>
                    <td>
                      <template v-if="row.rule_id">
                        <div>{{ ruleLabel(row.rule_id) }}</div>
                        <small class="text-muted">{{ row.rule_id }}</small>
                      </template>
                      <span v-else class="text-muted">--</span>
                    </td>
                    <td>
                      <span v-if="row.action">{{ priorityText(row.action) }}</span>
                      <span v-else class="text-muted">--</span>
                    </td>
                    <td>
                      <span v-if="actionBadge(row.action)" class="badge-action" :class="`sev-${actionBadge(row.action).severity}`">
                        {{ actionBadge(row.action).label }}
                      </span>
                      <span v-else class="text-muted">--</span>
                    </td>
                    <td>{{ tierLabel(row.kyc_tier) }}</td>
                    <td><a href="javascript:void(0)" class="view-log" @click="viewLog(row)">View Log</a></td>
                  </tr>
                  <tr v-if="pagedEvals.length === 0 && !evalLoading">
                    <td colspan="9" class="text-center py-4 text-muted">No data to display.</td>
                  </tr>
                  <tr v-if="evalLoading">
                    <td colspan="9" class="text-center py-4 text-muted">Loading…</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div class="d-flex align-items-center justify-content-between mt-3" v-if="evalTotal > perPage">
              <div class="d-flex align-items-center gap-2">
                <span>Display</span>
                <select class="form-select w-auto" v-model="perPage"><option v-for="o in pageOptions" :key="o" :value="o">{{ o }}</option></select>
              </div>
              <b-pagination class="pagination-rounded mb-0" v-model="page" :total-rows="evalTotal" :per-page="perPage"></b-pagination>
            </div>
          </div>

          <!-- ============ LOGS TAB ============ -->
          <div v-if="activeTab === 'logs'">
            <div class="d-flex flex-wrap align-items-start gap-3 mb-4">
              <div class="search-group">
                <div class="input-group">
                  <input type="text" class="form-control" v-model="logSearchKeyword" @keyup.enter="onLogSearch" placeholder="Search account / business" />
                </div>
                <small class="d-block mt-1">Search Keyword</small>
              </div>
              <div class="filter-group">
                <flat-pickr v-model="logDateRange" :config="logRangeCfg" placeholder="Select date range" class="form-control" />
                <small class="d-block mt-1">Filter by date</small>
              </div>
              <div class="filter-group">
                <select class="form-select" v-model="logFilterStatus">
                  <option value="all">All</option>
                  <option value="freeze">Freeze</option>
                  <option value="enabled">Enabled</option>
                </select>
                <small class="d-block mt-1">Filter by Wallet Status</small>
              </div>
              <div class="d-flex gap-2">
                <button class="btn btn-pink" @click="onLogSearch"><i class="bx bx-search"></i> Search</button>
                <button class="btn btn-pink" @click="onLogExport"><i class="bx bx-upload"></i> Export</button>
              </div>
            </div>

            <div class="table-responsive">
              <table class="table align-middle tm-table">
                <thead>
                  <tr>
                    <th>Wallet Status</th>
                    <th>Account Number</th>
                    <th>Account Name</th>
                    <th>Business Name</th>
                    <th>Reason</th>
                    <th>Date &amp; Time</th>
                    <th>Modified By</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="l in pagedLogs" :key="l.id">
                    <td><strong>{{ statusText(l.action) }}</strong></td>
                    <td>{{ l.account_number }}</td>
                    <td>{{ l.account_name }}</td>
                    <td>{{ l.business_name }}</td>
                    <td>{{ l.reason }}</td>
                    <td>{{ l.created_at }}</td>
                    <td>{{ l.modified_by || '—' }}</td>
                  </tr>
                  <tr v-if="pagedLogs.length === 0 && !logLoading">
                    <td colspan="7" class="text-center py-4 text-muted">No data to display.</td>
                  </tr>
                  <tr v-if="logLoading">
                    <td colspan="7" class="text-center py-4 text-muted">Loading…</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="d-flex align-items-center justify-content-between mt-3" v-if="logTotal > logPerPage">
              <div class="d-flex align-items-center gap-2">
                <span>Display</span>
                <select class="form-select w-auto" v-model="logPerPage"><option v-for="o in pageOptions" :key="o" :value="o">{{ o }}</option></select>
              </div>
              <b-pagination class="pagination-rounded mb-0" v-model="logPage" :total-rows="logTotal" :per-page="logPerPage"></b-pagination>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ============ FREEZE / UNFREEZE MODAL ============ -->
    <b-modal v-model="modal.show" centered hide-header hide-footer no-close-on-backdrop size="md" body-class="p-0" @hidden="closeStatusModal">
      <div class="tm-modal" v-if="modal.row">
        <div class="tm-modal-head">
          <h5 class="mb-0 fw-bold">
            {{ modal.tab === 'documents'
              ? 'Documents History'
              : `Kankolek ${modal.mode === 'freeze' ? 'Freeze' : 'Unfreeze'} Account` }}
          </h5>
          <button class="btn-x" @click="closeStatusModal"><i class="bx bx-x"></i></button>
        </div>
        <p class="tm-modal-sub">
          {{ modal.tab === 'documents'
            ? 'View documents previously submitted for this account.'
            : (modal.mode === 'freeze'
              ? 'Before you freeze the Kankolek Wallet. Please let us know the reason.'
              : 'Before you enable the Kankolek Wallet. Please let us know the reason.') }}
        </p>

        <!-- modal tabs -->
        <div class="tm-modal-tabs">
          <button class="mtab" :class="{ active: modal.tab === 'details' }" @click="modal.tab = 'details'">User Details</button>
          <button class="mtab" :class="{ active: modal.tab === 'documents' }" @click="modal.tab = 'documents'">Documents History</button>
        </div>

        <!-- USER DETAILS -->
        <div v-if="modal.tab === 'details'">
          <div class="info-card">
            <div class="info-row"><span>Business Name:</span><strong>{{ modal.row.business_name }}</strong></div>
            <div class="info-row"><span>Account Number:</span><strong>{{ modal.row.account_number }}</strong></div>
            <div class="info-row"><span>Account Name:</span><strong>{{ modal.row.account_name }}</strong></div>
            <div class="info-row"><span>Remaining Balance:</span>
              <strong v-if="!modal.balanceLoading">{{ formatCurrency(modal.balance) }}</strong>
              <span v-else class="text-muted">Loading…</span>
            </div>
            <div class="info-row"><span>Account Type:</span><strong>{{ tierLabel(modal.row.kyc_tier) }}</strong></div>
            <div class="info-row">
              <span>Status:</span>
              <strong :class="modal.row.wallet_status === 'enabled' ? 'txt-enabled' : 'txt-freeze'">
                {{ modal.row.wallet_status === 'enabled' ? 'Active' : 'Freeze' }}
              </strong>
            </div>
            <div class="info-row" v-if="modal.mode === 'unfreeze'">
              <span>Reason for Freeze:</span><strong class="txt-freeze">{{ originalFreezeReason() }}</strong>
            </div>
          </div>

          <!-- Reason -->
          <div class="mb-3">
            <label class="form-label fw-bold">Reason<span class="text-danger">*</span></label>
            <select class="form-select" v-model="modal.reason">
              <option value="">Select Reason</option>
              <option v-for="r in (modal.mode === 'freeze' ? FREEZE_REASONS : UNFREEZE_REASONS)" :key="r" :value="r">{{ r }}</option>
            </select>
          </div>

          <!-- Unfreeze-only: Account Action + Account Type. Hidden until a reason is selected. -->
          <div class="row g-3 mb-3" v-if="modal.mode === 'unfreeze' && modal.reason">
            <div class="col-6">
              <label class="form-label fw-bold">Account Action<span class="text-danger">*</span></label>
              <select class="form-select" v-model="modal.accountAction">
                <option value="">Select Account Action</option>
                <option v-for="a in ACCOUNT_ACTIONS" :key="a.value" :value="a.value">{{ a.text }}</option>
              </select>
            </div>
            <div class="col-6">
              <label class="form-label fw-bold">Account Type<span class="text-danger">*</span></label>
              <select class="form-select" v-model="modal.accountType">
                <option value="">Select Account Type</option>
                <option v-for="t in ACCOUNT_TYPES" :key="t.value" :value="t.value">{{ t.text }}</option>
              </select>
            </div>
          </div>

          <!-- Freeze-only: Initial Report. Applies ONLY to Fraudulent Activity (required by default;
               supporting documents to follow). All other reasons have no Initial Report. -->
          <div class="form-check mb-2" v-if="modal.mode === 'freeze' && isFraudulentActivity">
            <input class="form-check-input" type="checkbox" id="initialReport" v-model="modal.initialReport" />
            <label class="form-check-label" for="initialReport">
              <strong>Initial Report:</strong> Supporting documents are to follow. Uncheck to attach them now.
            </label>
          </div>

          <!-- Uploader. Hidden until a reason is selected. -->
          <div class="mb-3" v-if="modal.reason">
            <label class="form-label fw-bold">
              Upload Supporting Documents<span class="text-danger" v-if="modal.mode === 'unfreeze' || freezeDocsRequired">*</span>
            </label>
            <div class="uploader" @click="$refs.fileInput.click()">
              <i class="bx bx-cloud-upload"></i>
              <div>Drag &amp; drop supporting files here, or <span class="browse">Browse Files</span></div>
              <small class="text-muted">Supported: Docs and Images (multiple) · Max {{ 15 }}MB per file</small>
              <input ref="fileInput" type="file" multiple class="d-none" @change="onFilePick" />
            </div>
            <div class="uploaded-head" v-if="modal.files.length">
              Uploaded Files: <span class="float-end">({{ modal.files.length }} attached)</span>
            </div>
            <div class="file-row" v-for="(f, i) in modal.files" :key="i">
              <i class="bx bxs-file"></i>
              <div class="flex-grow-1">
                <div>{{ f.name }} <small class="text-muted">{{ f.size_label }}</small></div>
                <div class="progress mt-1" v-if="!f.done" style="height:4px;">
                  <div class="progress-bar" :style="{ width: f.progress + '%' }"></div>
                </div>
              </div>
              <button class="btn-trash" @click="removeFile(i)"><i class="bx bx-trash"></i></button>
            </div>
          </div>

          <!-- Remarks. Hidden until a reason is selected. -->
          <div class="mb-3" v-if="modal.reason">
            <label class="form-label fw-bold">Remarks <small class="text-muted">(Optional)</small></label>
            <textarea class="form-control" rows="2" v-model="modal.remarks" placeholder="Enter remarks..."></textarea>
          </div>

          <button class="btn w-100 mb-2" :class="modal.mode === 'freeze' ? 'btn-freeze' : 'btn-pink'"
                   :disabled="!canConfirm || modal.submitting" @click="confirmStatus">
            <span v-if="modal.submitting"><i class="bx bx-loader-alt bx-spin"></i> Processing…</span>
            <span v-else>{{ modal.mode === 'freeze' ? 'Confirm Freeze' : 'Confirm' }}</span>
          </button>
          <button class="btn btn-outline w-100" @click="closeStatusModal" :disabled="modal.submitting">Cancel</button>
        </div>

        <!-- DOCUMENTS HISTORY -->
        <div v-else>
          <p class="text-muted small">View documents previously submitted by the merchant.</p>
          <div class="doc-card" v-for="(d, i) in modal.docHistory" :key="i">
            <div class="small"><strong>Date &amp; Time:</strong> {{ d.created_at }}</div>
            <div class="small"><strong>Edited by:</strong> {{ d.modified_by }}</div>
            <div class="small"><strong>Reason:</strong> {{ d.reason }}</div>
            <div class="small" v-if="d.account_action"><strong>Account Action:</strong> {{ d.account_action }}</div>
            <div class="small"><strong>Status:</strong> {{ d.status === 'freeze' ? 'Freeze' : 'Active' }}</div>
            <div class="small" v-if="d.account_type"><strong>Account Type:</strong> {{ tierLabel(d.account_type) }}</div>
            <div class="small"><strong>Remarks:</strong> {{ d.remarks }}</div>
            <div class="small" v-if="d.reason_for_freeze !== null"><strong>Reason For Freeze:</strong> {{ d.reason_for_freeze }}</div>
            <div class="small mt-1">Supporting Documents: <span class="float-end">({{ d.documents.length }} attached)</span></div>
            <div class="file-row" v-for="(doc, j) in d.documents" :key="j">
              <i class="bx bxs-file"></i>
              <div class="flex-grow-1">{{ doc.file_name }} <small class="text-muted">{{ doc.size_label }}</small></div>
              <a href="javascript:void(0)" class="download-link" @click="downloadDoc(doc.document_id)">Download</a>
            </div>
          </div>
          <div v-if="modal.docHistory.length === 0" class="text-center text-muted py-4">No documents submitted yet.</div>
          <button class="btn btn-outline w-100 mt-2" @click="closeStatusModal">Close</button>
        </div>
      </div>
    </b-modal>

    <!-- ============ VIEW LOG MODAL ============ -->
    <b-modal v-model="logModal.show" centered hide-header hide-footer no-close-on-backdrop size="lg" body-class="p-0" @hidden="closeLogModal">
      <div class="tm-modal">
        <div class="tm-modal-head">
          <h5 class="mb-0 fw-bold">Activity Logs: Wallet Freezing</h5>
          <button class="btn-x" @click="closeLogModal"><i class="bx bx-x"></i></button>
        </div>
        <div class="d-flex flex-wrap gap-3 mb-3">
          <div class="filter-group flex-grow-1">
            <flat-pickr v-model="logModal.dateRange" :config="logModalRangeCfg" placeholder="Select date range" class="form-control" />
            <small class="d-block mt-1">Filter by date</small>
          </div>
          <div class="filter-group flex-grow-1">
            <select class="form-select" v-model="logModal.filterStatus">
              <option value="all">All</option>
              <option value="enabled">Enabled</option>
              <option value="freeze">Freeze</option>
            </select>
            <small class="d-block mt-1">Filter by Wallet Status</small>
          </div>
          <div class="d-flex gap-2 align-items-start">
            <button class="btn btn-pink" @click="onLogModalSearch"><i class="bx bx-search"></i> Search</button>
            <button class="btn btn-pink" @click="onLogModalExport"><i class="bx bx-upload"></i> Export</button>
          </div>
        </div>
        <div class="table-responsive">
          <table class="table align-middle tm-table">
            <thead>
              <tr><th>Wallet Status</th><th>Reason</th><th>Date &amp; Time</th><th>Modified By</th></tr>
            </thead>
            <tbody>
              <tr v-for="(l, i) in logModal.logs" :key="i">
                <td><strong>{{ statusText(l.action) }}</strong></td>
                <td>{{ l.reason }}</td>
                <td>{{ l.created_at }}</td>
                <td>{{ l.modified_by || '—' }}</td>
              </tr>
              <tr v-if="logModal.logs.length === 0 && !logModal.loading">
                <td colspan="4" class="text-center py-4 text-muted">No activity logs for this account.</td>
              </tr>
              <tr v-if="logModal.loading"><td colspan="4" class="text-center py-4 text-muted">Loading…</td></tr>
            </tbody>
          </table>
        </div>
        <div class="d-flex align-items-center justify-content-between mt-3" v-if="logModal.total > logModal.perPage">
          <div class="d-flex align-items-center gap-2">
            <span>Display</span>
            <select class="form-select w-auto" v-model="logModal.perPage"><option v-for="o in pageOptions" :key="o" :value="o">{{ o }}</option></select>
          </div>
          <b-pagination class="pagination-rounded mb-0" v-model="logModal.page" :total-rows="logModal.total" :per-page="logModal.perPage"></b-pagination>
        </div>
        <div class="d-flex justify-content-end mt-3">
          <button class="btn btn-outline" @click="closeLogModal">Close</button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<style scoped>
/* Tabs */
.tm-tabs { border-bottom: 2px solid #edecec; display: flex; gap: 0; }
.tab-btn { background: none; border: none; border-bottom: 2px solid transparent; padding: 10px 16px; font-size: 14px; font-weight: 500; color: #000; cursor: pointer; margin-bottom: -2px; }
.tab-btn.active { color: #e83092; border-bottom-color: #e83092; }
.tab-btn:hover:not(.active) { color: #7f4697; }

/* Buttons */
.btn-pink { background: #e83092; color: #fff; border: none; border-radius: 4px; padding: 8px 16px; font-size: 14px; font-weight: 500; display: inline-flex; align-items: center; justify-content: center; gap: 6px; }
.btn-pink:hover { background: #d12882; color: #fff; }
.btn-pink:disabled { opacity: .6; }
.btn-refresh { background: #7f4697; color: #fff; border: none; border-radius: 4px; padding: 8px 16px; font-weight: 500; }
.btn-freeze { background: #B91C1C; color: #fff; border: none; }
.btn-freeze:hover { background: #991b1b; color: #fff; }
.btn-freeze:disabled { opacity: .6; }
.btn-outline { background: #fff; border: 1px solid #e0e0e0; color: #485056; }
.btn-outline:hover { background: #f5f5f5; }

/* Filters */
.search-group .input-group { width: 300px; }
.search-field-select { max-width: 120px; }
.filter-group .form-select { min-width: 160px; }
.filter-group small, .search-group small { color: #6c757d; text-align: right; }

/* Table */
.tm-table thead th { font-weight: 700; font-size: 12px; color: #000; border-bottom: 1px solid #e0e0e0; white-space: nowrap; }
.tm-table tbody td { font-size: 12px; color: #202020; vertical-align: middle; border-bottom: 1px solid #e0e0e0; }

/* Toggle */
.status-toggle { display: flex; align-items: center; gap: 6px; }
.switch { position: relative; display: inline-block; width: 29px; height: 16px; margin: 0; }
.switch input { opacity: 0; width: 0; height: 0; }
.slider { position: absolute; inset: 0; border-radius: 39px; transition: .3s; cursor: pointer; }
.slider::before { content: ""; position: absolute; height: 13px; width: 13px; border-radius: 50%; background: #fff; top: 1.5px; transition: .3s; }
.slider.on { background: #7f4697; }
.slider.on::before { left: 14px; }
.slider.off { background: #831118; }
.slider.off::before { left: 1.5px; }
.txt-enabled { color: #508f30; font-weight: 600; }
.txt-freeze { color: #B91C1C; font-weight: 600; }

/* Badges */
.badge-action { font-size: 11px; font-weight: 600; padding: 4px 10px; border-radius: 12px; display: inline-block; }
.sev-low { background: #fff7d6; color: #8a6d00; }
.sev-medium { background: #e7f0ff; color: #1d4ed8; }
.sev-high { background: #ffe9d6; color: #c2410c; }
.sev-critical { background: #fde8e8; color: #B91C1C; }
.badge-product { font-size: 11px; font-weight: 600; padding: 3px 10px; border-radius: 12px; }
.badge-product.peddlr_lite { background: #ede7f6; color: #7f4697; }
.badge-product.peddlr { background: #e0f2f1; color: #00796b; }

.view-log { color: #e83092; font-weight: 500; text-decoration: underline; font-size: 12px; }

/* Modal */
.tm-modal { padding: 24px; }
.tm-modal-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; }
.tm-modal-head h5 { font-size: 20px; }
.btn-x { background: none; border: none; font-size: 24px; color: #485056; cursor: pointer; line-height: 1; }
.tm-modal-sub { font-size: 14px; color: #485056; margin-bottom: 16px; }
.tm-modal-tabs { display: flex; gap: 0; border-bottom: 1px solid #edecec; margin-bottom: 16px; }
.mtab { background: none; border: none; border-bottom: 2px solid transparent; padding: 8px 14px; font-size: 13px; font-weight: 500; color: #000; cursor: pointer; margin-bottom: -1px; }
.mtab.active { color: #e83092; border-bottom-color: #e83092; }

.info-card { background: #f9f9f9; border: 1px solid #e6eaf1; border-radius: 8px; padding: 8px 16px; margin-bottom: 16px; }
.info-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #e6eaf1; font-size: 13px; }
.info-row:last-child { border-bottom: none; }
.info-row span { color: #6c757d; }

.uploader { border: 1.5px dashed #e0a9cf; border-radius: 8px; padding: 20px; text-align: center; cursor: pointer; color: #6c757d; }
.uploader i { font-size: 28px; color: #e83092; }
.uploader .browse { color: #e83092; text-decoration: underline; }
.uploaded-head { font-size: 12px; margin-top: 10px; font-weight: 600; }
.file-row { display: flex; align-items: center; gap: 8px; padding: 8px 0; border-bottom: 1px solid #f0f0f0; font-size: 12px; }
.file-row i.bxs-file { font-size: 20px; color: #e83092; }
.btn-trash { background: none; border: none; color: #B91C1C; cursor: pointer; }
.download-link { color: #e83092; text-decoration: underline; font-size: 12px; }

.doc-card { border: 1px solid #e6eaf1; border-radius: 8px; padding: 12px; margin-bottom: 12px; }
.doc-card .small { padding: 2px 0; }
</style>
